import { DermloopClinicalActionTypeEnum } from '@dermloop/static-values';
import { Expose, Transform } from 'class-transformer';
import {
  IsBoolean,
  IsDate,
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
} from 'class-validator';
import { ModelViewerLocation } from '../model-viewer-location.dto';

export class ConditionSearchQueryResult {
  @Expose()
  @IsUUID()
  condition_id: string;

  @Expose()
  @IsUUID()
  condition_type_id: string;

  @Expose()
  @IsUUID()
  health_record_id: string;

  @Expose()
  @IsOptional()
  @IsNotEmpty()
  @Transform(({ value }) => (value ? JSON.parse(value) : null))
  precise_location?: ModelViewerLocation;

  @Expose()
  @IsUUID()
  gender_id: string;

  @Expose()
  @IsString()
  gender_name: string;

  @Expose()
  @IsOptional()
  @IsString()
  ssn?: string;

  @Expose()
  @IsOptional()
  @IsUUID()
  latest_skin_type_id?: string;

  @Expose()
  @IsOptional()
  @IsString()
  latest_skin_type_name?: string;

  @Expose()
  @IsString()
  @IsUUID('all')
  triage_id: string;

  @Expose()
  @IsNotEmpty()
  @IsUUID('all')
  triage_created_by_user_id: string;

  @Expose()
  @IsOptional()
  @IsString()
  @Transform(({ value }) => value?.toISOString())
  triage_created?: string;

  @Expose()
  @IsString()
  @IsOptional()
  @Transform(({ value }) => value?.toISOString())
  triage_updated?: string;

  @Expose()
  @IsOptional()
  @IsUUID()
  latest_dermoscopic_image_id?: string;

  @Expose()
  @IsOptional()
  @IsUUID()
  latest_dermoscopic_image_medical_unit_id?: string;

  @Expose()
  @IsOptional()
  @IsUUID()
  user_latest_dermoscopic_image_id?: string;

  @Expose()
  @IsOptional()
  @IsUUID()
  user_latest_dermoscopic_image_medical_unit_id?: string;

  @Expose()
  @IsOptional()
  @IsUUID()
  latest_clinical_image_id?: string;

  @Expose()
  @IsOptional()
  @IsUUID()
  latest_clinical_image_medical_unit_id?: string;

  @Expose()
  @IsOptional()
  @IsUUID()
  user_latest_clinical_image_id?: string;

  @Expose()
  @IsOptional()
  @IsUUID()
  user_latest_clinical_image_medical_unit_id?: string;

  @Expose()
  @IsOptional()
  @IsUUID('all')
  assessment_id?: string;

  @Expose()
  @IsOptional()
  @IsString()
  assessment_type?: 'pathology' | 'tele';

  @Expose()
  @IsOptional()
  @IsUUID('all')
  assessment_diagnosis_id?: string;

  @Expose()
  @IsOptional()
  @IsString()
  assessment_diagnosis_name?: string;

  @Expose()
  @IsOptional()
  @IsString()
  assessment_diagnosis_legacy_identifier?: string;

  @Expose()
  @IsOptional()
  @IsUUID('all')
  assessment_secondary_diagnosis_id?: string;

  @Expose()
  @IsOptional()
  @IsString()
  assessment_secondary_diagnosis_name?: string;

  @Expose()
  @IsOptional()
  @IsString()
  assessment_secondary_diagnosis_legacy_identifier?: string;

  @Expose()
  @IsOptional()
  @IsUUID('all')
  assessment_tertiary_diagnosis_id?: string;

  @Expose()
  @IsOptional()
  @IsString()
  assessment_tertiary_diagnosis_name?: string;

  @Expose()
  @IsOptional()
  @IsString()
  assessment_tertiary_diagnosis_legacy_identifier?: string;

  @Expose()
  @IsOptional()
  @IsString()
  assessment_proposed_clinical_action?: string;

  @Expose()
  @IsOptional()
  @IsDate()
  assessment_created?: Date;

  @Expose()
  @IsOptional()
  @IsUUID('all')
  latest_diagnosis_group_id?: string;

  @Expose()
  @IsOptional()
  @IsString()
  latest_diagnosis_group_name?: string;

  @Expose()
  @IsOptional()
  @IsString()
  latest_diagnosis_group_article_id?: string;

  @Expose()
  @IsOptional()
  @IsUUID('all')
  latest_diagnosis_id?: string;

  @Expose()
  @IsOptional()
  @IsString()
  latest_diagnosis_name?: string;

  @Expose()
  @IsOptional()
  @IsString()
  latest_diagnosis_legacy_identifier?: string;

  @Expose()
  @IsOptional()
  @IsUUID('all')
  latest_differential_diagnosis_group_id?: string;

  @Expose()
  @IsOptional()
  @IsString()
  latest_differential_diagnosis_group_name?: string;

  @Expose()
  @IsOptional()
  @IsString()
  latest_differential_diagnosis_group_article_id?: string;

  @Expose()
  @IsOptional()
  @IsUUID('all')
  latest_differential_diagnosis_id?: string;

  @Expose()
  @IsOptional()
  @IsString()
  latest_differential_diagnosis_name?: string;

  @Expose()
  @IsOptional()
  @IsString()
  latest_differential_diagnosis_legacy_identifier?: string;

  @Expose()
  @IsOptional()
  @IsBoolean()
  correct_diagnosis?: boolean;

  @Expose()
  @IsString()
  @IsOptional()
  @IsEnum(DermloopClinicalActionTypeEnum)
  latest_action?: DermloopClinicalActionTypeEnum;

  @Expose()
  @IsString()
  @IsOptional()
  @Transform(({ value }) => value?.toString())
  latest_action_details?: string;

  @Expose()
  @IsBoolean()
  @Transform(({ value }) => !!parseInt(value))
  user_can_edit_latest_triage: boolean;

  @Expose()
  @IsBoolean()
  user_latest_triage_draft: boolean;

  @Expose()
  @IsNotEmpty()
  @IsOptional()
  @IsString()
  @Transform(({ value }) => value?.toString())
  user_dermoscopic_image_id?: string;

  @Expose()
  @IsNumber()
  @IsOptional()
  user_focal_point_x?: number;

  @Expose()
  @IsNumber()
  @IsOptional()
  user_focal_point_y?: number;

  @Expose()
  @IsString()
  @IsOptional()
  @Transform(({ value }) => value?.toString())
  user_image_medical_unit_id?: string;

  @Expose()
  @IsNumber()
  @IsOptional()
  latest_focal_point_x?: number;

  @Expose()
  @IsNumber()
  @IsOptional()
  latest_focal_point_y?: number;
}
